import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import PlatformIcon from '@saucelabs/sl-component-platform-icon';
import { getOSNameAndVersion } from '@saucelabs/sl-util-helpers';
import { useInView } from 'react-intersection-observer';
import Placeholder from '@saucelabs/sl-images/lib/placeholder.png';
import { Typography } from '@saucelabs/ds-typography';
import ReactImageFallback from 'react-image-fallback';
import { Button } from '@saucelabs/ds-button';
import { IconManualFill } from '@saucelabs/ds-icons';
import { ModalContext } from '@saucelabs/ds-modal';
import { Badge } from '@saucelabs/ds-badge';

import { Preset } from '../records';
import selectors from '../selectors';
import utils from '../utils';
import { operations } from '..';
import ThumbnailAndroid from '../../../images/thumbnail-android.svg';
import ThumbnailIOS from '../../../images/thumbnail-iOS.svg';
import { APP_STORAGE_KIND } from '../../../constants';
import TunnelIndicator from './TunnelIndicator';
import {
  records as realDeviceSelectionRecords,
  selectors as realDeviceSelectionSelectors,
  constants as realDeviceSelectionConstants,
} from '../../realDeviceSelection';
import {
  EVENTS,
  LIVE_TESTING_TRACKING_PROPS,
  LIVE_TESTING_PRODUCT_SUB_AREAS,
  LIVE_TESTING_SUBJECT_NAMES,
  trackEvent,
} from '../../../tracking';
import Styles from './PresetItem.styles';
import { DeviceDescriptor } from '../../realDeviceSelection/records';

const DefaultAppIcon = Object.freeze({
  [APP_STORAGE_KIND.IOS]: <ThumbnailIOS />,
  [APP_STORAGE_KIND.ANDROID]: <ThumbnailAndroid />,
});

const trackStartTestClick = (index = 0) => trackEvent(EVENTS.OBJECT_CLICKED, {
  subject_name: LIVE_TESTING_SUBJECT_NAMES.TEST_STARTED_FROM_RECENTS,
  product_sub_area: LIVE_TESTING_PRODUCT_SUB_AREAS.RECENTS_START_TEST_BUTTON,
  state: index + 1,
  ...LIVE_TESTING_TRACKING_PROPS,
});

interface PresetItemProps {
  index: number,
  preset: Preset,
}

const PresetItem: React.FC<PresetItemProps> = ({ preset, index }) => {
  const [imageUrlBroken, setImageUrlBroken] = useState(false);
  const dispatch = useDispatch();
  const app = useSelector(selectors.getAppForPreset(preset));
  const [ref, inView] = useInView({ triggerOnce: true });
  const { hideModal } = useContext(ModalContext); // eslint-disable-line @typescript-eslint/unbound-method
  const sortedDevices = useSelector(
    realDeviceSelectionSelectors.sortedDevicesSelector,
  );

  const availableDevices: string[] = useSelector(
    realDeviceSelectionSelectors.availableDevicesSelector,
  );
  const { isLoading } = useSelector(selectors.presetsSelector);

  const {
    browserConfiguration,
    realDeviceConfiguration,
    url,
    tunnel,
    virtualDeviceConfiguration,
  } = preset.payload;

  // VMD-related helper variables
  const vmdOsDisplay: string | undefined = virtualDeviceConfiguration?.osDisplay as string;
  const vmdOsVersion: string | undefined = virtualDeviceConfiguration?.version as string;
  // RDC-related helper variables

  let realDeviceDescriptor;
  if (realDeviceConfiguration) {
    const potentialDevice: string | undefined = realDeviceSelectionConstants.DEVICE_PREFIX_REGEX
      .exec(
      realDeviceConfiguration.descriptorId as string)?.[0] ??
      realDeviceConfiguration.descriptorId as string;
    const matchedAvailableDevice: DeviceDescriptor = sortedDevices.PAID.find(
      (device: DeviceDescriptor) : boolean => device.descriptorId.includes(potentialDevice));

    realDeviceConfiguration.descriptorId = matchedAvailableDevice?.descriptorId ??
    realDeviceConfiguration.descriptorId;

    realDeviceDescriptor = new realDeviceSelectionRecords.DeviceDescriptor(realDeviceConfiguration);
  }

  const realDeviceAvailable = availableDevices.includes(realDeviceDescriptor?.descriptorId ?? '');

  const appSpecs = app?.getAppSpecification();
  const isDeviceInUse = preset.isRdcDevice() ? !realDeviceAvailable : false;

  const startTest = () => {
    dispatch(operations.startSessionFromPreset(preset, app));
    trackStartTestClick(index);
    hideModal?.();
  };

  return (
    <div
      className="
        flex flex-row relative group
        border border-b-0 border-solid border-gray-300 last:border-b
        hover:bg-gray-50
        focus-within:bg-gray-50"
      data-qa="preset-item"
    >

      {/* icons section */}
      <div className="p-3">
        {
          preset.isVdcDevice() && (
            <div className="flex flex-col items-center">
              <PlatformIcon
                className="w-8 h-8"
                hasColor
                name={browserConfiguration?.browser as string}
              />
              <PlatformIcon
                className="w-2 h-2 mt-1"
                hasColor
                name={getOSNameAndVersion(browserConfiguration?.os as string).name}
              />
            </div>
          )
        }
        {
          preset.isVmdDevice() && preset.isWebTest() && (
            <PlatformIcon
              className="w-8 h-8"
              hasColor
              name={virtualDeviceConfiguration?.browser as string}
            />
          )
        }
        {
          preset.isVmdDevice() && preset.isAppTest() && (
            <div className="flex flex-col items-center">
              <div className="w-8 h-8">
                {appSpecs && (
                  <ReactImageFallback
                    alt="app icon"
                    className="w-full h-full"
                    fallbackImage={DefaultAppIcon[appSpecs.kind]}
                    src={appSpecs.iconUrl}
                  />
                )}
              </div>
              <PlatformIcon
                className="w-2 h-2 mt-1"
                hasColor
                name={virtualDeviceConfiguration?.browser as string}
              />
            </div>
          )
        }
        {
          preset.isRdcDevice() && preset.isWebTest() && (
            <div className="flex flex-col items-center">
              <div
                ref={ref}
                className="w-8 h-8"
              >
                {!inView && (
                  <img
                    alt={realDeviceDescriptor?.name}
                    src={Placeholder}
                  />
                )}
                {inView && (
                  <img
                    alt={realDeviceDescriptor?.name}
                    className="block m-auto max-w-full max-h-full"
                    onError={() => setImageUrlBroken(true)}
                    src={imageUrlBroken ? Placeholder : realDeviceDescriptor?.getImageUrl()}
                  />
                )}
              </div>
              <PlatformIcon
                className="w-2 h-2 mt-1"
                hasColor
                name={realDeviceConfiguration?.os as string}
              />
            </div>
          )
        }
        {
          preset.isRdcDevice() && preset.isAppTest() && (
            <div className="flex flex-col items-center">
              <div
                ref={ref}
                className="relative w-8 h-8"
              >
                {!inView && (
                  <img
                    alt={realDeviceDescriptor?.name}
                    src={Placeholder}
                  />
                )}
                {inView && (
                  <img
                    alt={realDeviceDescriptor?.name}
                    className="block m-auto max-w-full max-h-full"
                    onError={() => setImageUrlBroken(true)}
                    src={imageUrlBroken ? Placeholder : realDeviceDescriptor?.getImageUrl()}
                  />
                )}
                <div className="absolute top-1.5 left-1/2 w-5 h-5">
                  {appSpecs && (
                    <ReactImageFallback
                      alt="app icon"
                      className="w-full h-full"
                      fallbackImage={DefaultAppIcon[appSpecs.kind]}
                      src={appSpecs.iconUrl}
                    />
                  )}
                </div>
              </div>
              <PlatformIcon
                className="w-2 h-2 mt-1"
                hasColor
                name={realDeviceConfiguration?.os as string}
              />
            </div>
          )
        }
      </div>

      {/* text section */}
      <div className={`
        flex flex-col justify-between
        w-full mr-3
        ${Styles['break-long-strings']}`}
      >

        {/* top aligned */}
        <div className="pt-3 pl-1">
          {/* target */}
          <div>
            <Typography
              className={classnames('font-bold', {
                'text-high-emphasis': !isDeviceInUse,
                'text-medium-emphasis': isDeviceInUse,
              })}
              component="span"
              variant="body-large"
            >
              { preset.isWebTest() && url }
              { preset.isAppTest() && appSpecs?.appName }
            </Typography>
            <TunnelIndicator
              presetId={preset.id}
              tunnel={tunnel}
            />
          </div>

          {/* app data */}
          { preset.isAppTest() && (
            <Typography
              className="text-high-emphasis"
              component="div"
              variant="body"
            >
              { appSpecs && `
                ${appSpecs.fileName}
                (v${appSpecs.version}, Build\u00A0${appSpecs.build})
              `}
            </Typography>
          )}

          {/* platform */}
          <Typography
            className="text-medium-emphasis"
            component="div"
            variant="body"
          >
            { preset.isVdcDevice() && browserConfiguration?.osVersionDisplay }
            { preset.isVmdDevice() && utils.createPrettyVmdPlatform(vmdOsDisplay, vmdOsVersion) }
            { preset.isRdcDevice() && realDeviceDescriptor?.getPrettyOsAndOsVersion() }
          </Typography>

          {/* environment */}
          <Typography
            className="text-medium-emphasis"
            component="div"
            variant="body"
          >
            { preset.isVdcDevice() && `${browserConfiguration?.browserDisplay as string} ${browserConfiguration?.browserVersionDisplay as string}`}
            { preset.isVmdDevice() && virtualDeviceConfiguration?.device }
            { preset.isRdcDevice() && (
              <>
                <span>{realDeviceDescriptor?.name}</span>
                {isDeviceInUse && (
                  <Badge
                    className="ml-1"
                    color="orange"
                    emphasis="high"
                  >
                    In Use
                  </Badge>
                )}
              </>
            ) }
          </Typography>

          {preset.isVdcDevice() && (
            <Typography
              className="text-medium-emphasis"
              variant="body"
            >
              {browserConfiguration?.res}
            </Typography>
          )}
        </div>

        {/* bottom aligned */}
        <div className="pb-3 pt-1">
          {/* date */}
          <Typography
            className="text-medium-emphasis text-right"
            component="div"
            variant="body"
          >
            <span>{ preset.getPrettyLastAccessDate() }</span>
          </Typography>
        </div>
      </div>

      {/* hover overlay */}
      <div className={`
        absolute top-0 right-0 opacity-0 p-3 bg-gray-50
        group-hover:opacity-100
        focus-within:opacity-100
        ${Styles['hover-overlay']}
        `}
      >
        <Button
          data-qa="preset-item-start-test-button"
          disabled={isDeviceInUse || isLoading}
          onPress={startTest}
          prefixIcon={<IconManualFill />}
          variant="text"
        >
          Start Test
        </Button>
      </div>
    </div>
  );
};

export default PresetItem;
